import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { membershipPlans } from '../mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, Crown, Zap, Star, Loader2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const Membership = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(null); // Track which plan is loading
  const [subStatus, setSubStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      if (!currentUser) return;
      try {
        const res = await api.get('/api/subscriptions/status');
        if (!cancelled) setSubStatus(res.data);
      } catch (err) {
        // 401/404 is fine for guests, ignore
      }
    };
    loadStatus();
    return () => { cancelled = true; };
  }, [currentUser]);

  const handleSubscribe = async (plan) => {
    if (!currentUser) {
      toast.error('Please sign in to subscribe');
      return;
    }

    setLoading(plan.id);

    try {
      const packageId = plan.id === 1 ? 'sauce_sity_regular' : 'the_gentleman';
      const originUrl = window.location.origin;

      // Subscription mode (Stripe handles recurring billing)
      const response = await api.post('/api/subscriptions/checkout', {
        package_id: packageId,
        origin_url: originUrl,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post('/api/subscriptions/portal');
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const getMembershipBadge = () => {
    if (!currentUser?.membershipTier || currentUser.membershipTier === 'none') {
      return null;
    }
    
    if (currentUser.membershipTier === 'vip') {
      return (
        <Badge className="bg-gradient-to-r from-lime-500 to-lime-600 text-black border-purple-500">
          <Crown className="w-3 h-3 mr-1" />
          VIP Member
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-purple-600 text-lime-400">
        <Star className="w-3 h-3 mr-1" />
        Sauce Member
      </Badge>
    );
  };

  return (
    <div className="sauce-sity-page" data-testid="membership-page">
      <div className="sauce-sity-content">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="ss-title-xl mb-4">
            <span className="ss-highlight-purple">Membership</span> Plans
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Get priority access, exclusive perks, and earn rewards with every visit
          </p>
          {currentUser && getMembershipBadge() && (
            <div className="mt-6 flex justify-center">
              {getMembershipBadge()}
            </div>
          )}
        </div>

        {/* Active Subscription Status & Manage Button */}
        {currentUser && subStatus?.has_active_subscription && (
          <div className="max-w-3xl mx-auto mb-12 rounded-xl bg-zinc-900 border border-lime-500/30 p-6" data-testid="subscription-status-card">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-zinc-400 mb-1">Your Membership</p>
                <p className="text-xl font-bold text-white">
                  {subStatus.membership_tier === 'vip' ? 'The Gentleman' : 'Sauce Sity Regular'}
                  <span className={`ml-3 text-xs px-2 py-1 rounded font-semibold ${
                    subStatus.subscription_status === 'active' ? 'bg-lime-500 text-black' :
                    subStatus.subscription_status === 'past_due' ? 'bg-yellow-500 text-black' :
                    'bg-zinc-700 text-zinc-300'
                  }`}>
                    {subStatus.subscription_status === 'active' ? 'Active' :
                     subStatus.subscription_status === 'past_due' ? 'Payment Issue' :
                     subStatus.subscription_status}
                  </span>
                </p>
                {subStatus.current_period_end && (
                  <p className="text-sm text-zinc-400 mt-2">
                    {subStatus.cancel_at_period_end ? 'Cancels on' : 'Renews on'}{' '}
                    <strong>{new Date(subStatus.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                  </p>
                )}
              </div>
              <Button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                data-testid="manage-subscription-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
              >
                {portalLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…</>
                ) : (
                  <><Settings className="w-4 h-4 mr-2" /> Manage Subscription</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Membership Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {membershipPlans.map(plan => (
            <Card 
              key={plan.id}
              className={`bg-zinc-900 border-2 relative overflow-hidden ${
                plan.featured 
                  ? 'border-lime-500 scale-105' 
                  : 'border-zinc-800 hover:border-purple-500'
              } transition-all duration-300`}
            >
              {plan.featured && (
                <div className="absolute top-0 right-0 bg-gradient-to-br from-lime-500 to-lime-600 text-black px-4 py-1 text-sm font-semibold">
                  MOST POPULAR
                </div>
              )}
              
              <CardHeader className="text-center pt-8">
                <div className="flex justify-center mb-4">
                  {plan.featured ? (
                    <div className="bg-lime-500/20 p-4 rounded-full">
                      <Crown className="w-12 h-12 text-lime-400" />
                    </div>
                  ) : (
                    <div className="bg-purple-500/20 p-4 rounded-full">
                      <Zap className="w-12 h-12 text-purple-400" />
                    </div>
                  )}
                </div>
                <CardTitle className="text-3xl font-bold text-white mb-2">
                  {plan.name}
                </CardTitle>
                <div className="flex items-baseline justify-center space-x-2">
                  <span className={`text-5xl font-bold ${plan.featured ? 'text-lime-400' : 'text-purple-400'}`}>
                    ${plan.price}
                  </span>
                  <span className="text-zinc-400">/{plan.interval}</span>
                </div>
              </CardHeader>

              <CardContent className="px-8 pb-8">
                <ul className="space-y-4 mb-8">
                  {plan.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <Check className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${plan.featured ? 'text-lime-400' : 'text-purple-400'}`} />
                      <span className="text-zinc-300">{benefit}</span>
                    </li>
                  ))}
                </ul>

                {currentUser ? (
                  <Button
                    onClick={() => handleSubscribe(plan)}
                    disabled={loading === plan.id}
                    className={`w-full ${
                      plan.featured
                        ? 'bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700'
                        : 'bg-gradient-to-r from-purple-600 to-purple-700 text-lime-400 hover:from-purple-700 hover:to-purple-800'
                    } font-semibold border border-${plan.featured ? 'purple' : 'lime'}-500/30`}
                  >
                    {loading === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Subscribe Now'
                    )}
                  </Button>
                ) : (
                  <Link to="/signup">
                    <Button
                      className={`w-full ${
                        plan.featured
                          ? 'bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700'
                          : 'bg-gradient-to-r from-purple-600 to-purple-700 text-lime-400 hover:from-purple-700 hover:to-purple-800'
                      } font-semibold`}
                    >
                      Sign Up to Subscribe
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Why Become a Member */}
        <div className="bg-zinc-950 rounded-xl p-8 md:p-12 border border-zinc-800">
          <h2 className="text-3xl font-bold text-center mb-8 text-purple-400">Why Become a Member?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-purple-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-lime-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Priority Access</h3>
              <p className="text-zinc-400">Book appointments before non-members and secure your preferred time slots</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-lime-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Recurring Bookings</h3>
              <p className="text-zinc-400">Set and forget - automatically book your weekly, bi-weekly, or monthly appointments</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-lime-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">VIP Treatment</h3>
              <p className="text-zinc-400">Skip lines, get faster check-ins, and enjoy special member-only perks</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Membership;
